<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from "vue";

import BaseBoardInput from "./BaseBoardInput.vue";

const props = defineProps<{
  value: number;
  maxlength: number;
  noBorders?: boolean;
  allowFloat?: boolean;
}>();
const emit = defineEmits<{ input: [value: number]; inputOnBlur: [value: number] }>();

const rootRef = useTemplateRef("root");

const allowedChars = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "."];
const numbers = computed(() => (props.allowFloat ? allowedChars : allowedChars.slice(0, -1)));

const inputValue = ref(String(props.value));

// if parent updates prop
watch(
  () => props.value,
  (newVal) => {
    if (!isNaN(newVal)) {
      inputValue.value = String(newVal);
    }
  },
);

function parseAndEmit(val: string, type: "input" | "blur") {
  const numericVal = val.trim() === "" ? 0 : parseFloat(val);
  const cleanVal = isNaN(numericVal) ? 0 : numericVal;

  if (type === "input") {
    emit("input", cleanVal);
  } else {
    emit("inputOnBlur", cleanVal);
    inputValue.value = String(cleanVal);
  }
}

function keyPressed(event: KeyboardEvent) {
  if (!numbers.value.includes(event.key)) {
    event.preventDefault();
  }
}

function unfocus() {
  rootRef.value!.blur();
}

function onInput(event: Event) {
  const val = (event.target as HTMLInputElement).value;
  parseAndEmit(val, "input");
}

function onBlur(val: string) {
  parseAndEmit(val, "blur");
}
</script>

<template>
  <BaseBoardInput
    ref="root"
    v-model="inputValue"
    class="board-number-input"
    type="text"
    :maxlength
    :no-borders
    @keypress="keyPressed"
    @keydown.esc.enter="unfocus"
    @input="onInput"
    @input-on-blur="onBlur"
  />
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";

.board-number-input {
  &:read-only {
    border: none;
    cursor: default;
    background-color: colors.$background-disabled;
  }
}
</style>
